import { IDisposable, Terminal } from "@xterm/xterm";
import { Unicode11Addon } from '@xterm/addon-unicode11';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { WebglAddon } from '@xterm/addon-webgl';
import { ImageAddon } from '@xterm/addon-image';
import { ZModemAddon } from "./zmodem";

export class GoTTYXterm {
    // The HTMLElement that contains our terminal
    elem: HTMLElement;

    // The xtermjs.XTerm
    term: Terminal;

    resizeListener: () => void;

    message: HTMLElement;
    messageTimeout: number;
    messageTimer!: NodeJS.Timeout;

    onResizeHandler!: IDisposable;
    onDataHandler!: IDisposable;

    fitAddOn: FitAddon;
    zmodemAddon: ZModemAddon;
    toServer!: (data: string | Uint8Array) => void;
    encoder!: TextEncoder

    constructor(elem: HTMLElement, preferences: Record<string, unknown> = {}) {
        this.elem = elem;
        this.term = new Terminal({
            allowProposedApi: true,
            customGlyphs: true,
            rescaleOverlappingGlyphs: true,
        });

        const unicode11Addon = new Unicode11Addon();
        this.term.loadAddon(unicode11Addon);
        this.term.unicode.activeVersion = '11';

        this.setPreferences(preferences);

        this.fitAddOn = new FitAddon();
        this.zmodemAddon = new ZModemAddon({
            toTerminal: (x: Uint8Array) => this.term.write(x),
            toServer: (x: Uint8Array) => this.sendInput(x)
        });
        this.term.loadAddon(new WebLinksAddon());
        this.term.loadAddon(this.fitAddOn);
        this.term.loadAddon(new ImageAddon());
        this.term.loadAddon(this.zmodemAddon);

        this.message = elem.ownerDocument.createElement("div");
        this.message.className = "xterm-overlay";
        this.messageTimeout = 2000;

        this.resizeListener = () => {
            this.fitAddOn.fit();
            this.term.scrollToBottom();
            this.showMessage(String(this.term.cols) + "x" + String(this.term.rows), this.messageTimeout);
        };

        this.term.open(elem);

        try {
            this.term.loadAddon(new WebglAddon());
        } catch (e) {
            console.warn("WebGL renderer failed to load, using canvas fallback", e);
        }

        this.term.focus();
        this.resizeListener();

        window.addEventListener("resize", () => { this.resizeListener(); });
    };

    info(): { columns: number, rows: number } {
        return { columns: this.term.cols, rows: this.term.rows };
    };

    // This gets called from the Websocket's onReceive handler
    output(data: Uint8Array) {
        this.zmodemAddon.consume(data);
    };

    getMessage(): HTMLElement {
        return this.message;
    }

    showMessage(message: string, timeout: number) {
        this.message.innerHTML = message;
        this.showMessageElem(timeout);
    }

    showMessageElem(timeout: number) {
        this.elem.appendChild(this.message);

        if (this.messageTimer) {
            clearTimeout(this.messageTimer);
        }
        if (timeout > 0) {
            this.messageTimer = setTimeout(() => {
                try {
                    this.elem.removeChild(this.message);
                } catch (error) {
                    console.error(error);
                }
            }, timeout);
        }
    };

    removeMessage(): void {
        if (this.message.parentNode == this.elem) {
            this.elem.removeChild(this.message);
        }
    }

    setWindowTitle(title: string) {
        document.title = title;
    };

    setPreferences(value?: Record<string, unknown> | null) {
        if (!value) {
            return;
        }

        // Apply font settings first so the renderer initializes with the
        // correct font metrics before any renderer-specific logic.
        const keys = Object.keys(value);
        const fontKeys = keys.filter(k => k === "font-family" || k === "font-size");
        const otherKeys = keys.filter(k => k !== "font-family" && k !== "font-size");

        for (const key of [...fontKeys, ...otherKeys]) {
            switch (key) {
                case "font-family":
                    this.term.options.fontFamily = value[key] as string;
                    break;
                case "font-size":
                    this.term.options.fontSize = value[key] as number;
                    break;
                case "EnableWebGL":
                    // Already loaded by default in constructor — skip to avoid
                    // orphaning the first WebglAddon with a duplicate.
                    break;
                case "cursor-blink":
                    this.term.options.cursorBlink = value[key] as boolean;
                    break;
                case "cursor-style":
                    this.term.options.cursorStyle = value[key] as "block" | "underline" | "bar";
                    break;
                case "scrollback-lines":
                    this.term.options.scrollback = value[key] as number;
                    break;
                case "theme":
                    this.term.options.theme = value[key] as object;
                    break;
            }
        }
    };

    sendInput(data: Uint8Array) {
        return this.toServer(data)
    }

    onInput(callback: (input: string | Uint8Array) => void) {
        this.encoder = new TextEncoder()
        this.toServer = callback;

        // I *think* we're ok like this, but if not, we can dispose
        // of the previous handler and put the new one in place.
        if (this.onDataHandler !== undefined) {
            return
        }

        this.onDataHandler = this.term.onData((input) => {
            this.toServer(this.encoder.encode(input));
        });
    };

    onResize(callback: (colmuns: number, rows: number) => void) {
        this.onResizeHandler = this.term.onResize(() => {
            callback(this.term.cols, this.term.rows);
        });
    };

    deactivate(): void {
        this.onDataHandler.dispose();
        this.onResizeHandler.dispose();
        this.term.blur();
    }

    reset(): void {
        this.removeMessage();
        this.term.clear();
    }

    close(): void {
        window.removeEventListener("resize", this.resizeListener);
        this.term.dispose();
    }

    disableStdin(): void {
        this.term.options.disableStdin = true;
    }

    enableStdin(): void {
        this.term.options.disableStdin = false;
    }

    focus(): void {
        this.term.focus();
    }
}